import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button';

/**
 * ForgotPassword page for password recovery
 * @returns {JSX.Element} ForgotPassword page component
 */
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Handle email change
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError('');
      
      // Simulate API call for password reset (would be implemented in real app)
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      // Show success message
      setSubmitted(true);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-3xl font-extrabold text-blue-600">
          ConnectHub
        </h1>
        <h2 className="mt-2 text-center text-sm text-gray-600">
          Reset your password
        </h2>
      </div>
      
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {submitted ? (
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                Check your email
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                We've sent password reset instructions to{' '}
                <span className="font-medium text-gray-900">{email}</span>.
                Please check your inbox and spam folder.
              </p>
              <div className="mt-6">
                <Link to="/login">
                  <Button variant="light" isFullWidth>
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={handleEmailChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      error ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    placeholder="you@example.com"
                  />
                  {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                </div>
              </div>
              
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  isFullWidth
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                >
                  Reset Password
                </Button>
              </div>
              
              <div className="flex items-center justify-center">
                <div className="text-sm">
                  <Link
                    to="/login"
                    className="font-medium text-blue-600 hover:text-blue-500"
                  >
                    Back to login
                  </Link>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;